/*
 * 戒导航 · 登录防爆破（jiedh/login-guard）前台初始化
 * =============================================================================
 * 职责：让**论坛**的登录弹窗在「因登录失败次数过多被封禁（HTTP 429）」时，
 *       把服务端返回的剩余时间直接显示给用户，而不是只说一句「请求过于频繁」。
 *
 * 背景一：429 的文案是怎么来的 —— vendor/flarum/core/js/src/common/Application.tsx 的 requestErrorCatch()
 * -----------------------------------------------------------------------------
 *   const formattedErrors = error.response?.errors?.map((e) => decodeURI(e.detail ?? '')) ?? [];
 *   switch (error.status) {
 *     case 429:
 *       content = app.translator.trans('core.lib.error.rate_limit_exceeded_message');
 *       break;
 *   }
 *   ...
 *   error.alert = { type: 'error', content, controls: ... };        // ← 先把 alert 填好
 *   let useDefaultHandler = !customErrorHandler;
 *   if (customErrorHandler) { useDefaultHandler = (await customErrorHandler(error)) === false; }
 *   if (useDefaultHandler) this.requestErrorDefaultHandler(error, isDebug, formattedErrors);
 * 两个关键事实：
 *   a) **error.alert 在调用自定义 errorHandler 之前就已经被填好了**，且 429 分支完全不使用服务端 detail；
 *   b) 所以只要在 errorHandler 里覆盖 error.alert.content，用户看到的就是我们想要的文案。
 *
 * 背景二：我们的介入点 —— vendor/flarum/core/js/src/forum/components/LogInModal.tsx
 * -----------------------------------------------------------------------------
 *   onsubmit() { app.session.login(this.loginParams(), { errorHandler: this.onerror.bind(this) }) }
 *   onerror(error) {                       // ← 就是它
 *     if (error.status === 401 && error.alert) { error.alert.content = ...; this.password(''); }
 *     super.onerror(error);                // ← 最终由 FormModal 把 error.alert 显示到弹窗上
 *   }
 * 核心的 onerror 只特判 401，429 会原样落到上面那句「请求过于频繁」。
 * 因此这里**覆盖 LogInModal.prototype.onerror**：先补 429 的文案，再原样调用核心实现。
 *
 * ★ 背景三（本文件最关键的一处踩坑）：LogInModal 是「懒加载 chunk」，不能直接 import 它的取值
 * -----------------------------------------------------------------------------
 *   核心把 LogInModal 拆成了按需加载的 chunk（见 vendor/flarum/core/js/dist/forum/components/LogInModal.js，
 *   该文件末尾会执行 `flarum.reg.add("core","forum/components/LogInModal", 导出类)`）。
 *   而 flarum-webpack-config 会把 `import LogInModal from 'flarum/forum/components/LogInModal'`
 *   编译成 `flarum.reg.get("core","forum/components/LogInModal")`（见 flarum-webpack-config/src/index.cjs
 *   的 externals 配置），**在弹窗首次打开之前这个调用返回 undefined**，
 *   于是 `LogInModal.prototype` 会直接抛 TypeError，初始化器报错。
 *   正确做法（Flarum 2 官方为此提供了专门的支持）：
 *     · 用 **字符串路径** 调用 extend()/override()，核心源码 common/extend.ts 会对字符串走
 *       `flarum.reg.onLoad(namespace, id, (module) => extend(module.prototype, ...))` 分支 ——
 *       模块已经加载就立刻生效，尚未加载就排队等 chunk 到位后自动生效；
 *     · 等价写法是直接用 flarum.reg.onLoad(...)。
 *   本文件采用官方推荐的 override(字符串路径, ...) 写法，因此**看不到任何 LogInModal 的 import**，
 *   这不是遗漏，而是刻意为之。
 *
 * 数据来源（服务端）
 * -----------------------------------------------------------------------------
 *   packages/login-guard/src/ErrorHandler/LoginRateLimitedHandler.php 通过 HandledError::withDetails() 输出：
 *     { errors: [ { status: "429", code: "too_many_requests",
 *                   detail: "登录尝试过于频繁，请在 11 分 22 秒后重试",
 *                   remaining_seconds: 682 } ] }
 *   本文件读取 detail（人类可读文案），交给核心既有的 alert 展示链，改动面最小。
 *
 * 为什么用「运行时包装原型方法」而不是改 vendor/：
 *   项目硬约束 —— vendor/ 是 composer 管理的核心，升级时必须保持原样。
 *   包装的最大风险是核心未来改了实现，本扩展最多退化成「不显示剩余时间」，
 *   绝不会报错、不会影响正常登录（所有分支都有兜底与 try/catch）。
 *
 * 首页登录弹窗不归本文件管：它由 packages/nav/views/nav.php 的 jnSafeErrorMessage() 负责，
 * 两处刻意统一「优先服务端 detail、无 detail 时回落通用文案」的口径。
 *
 * ★ 另一项职责：退出登录后「停留在当前页」而不是跳回首页（2026-10-28 用户需求）
 * -----------------------------------------------------------------------------
 * 核心 Session.logout()（vendor/flarum/core/js/src/common/Session.ts）只做了三件事：
 *   建 <form method="POST" action="{baseUrl}/logout">、塞一个 csrfToken 隐藏域、form.submit()。
 * **不带任何 return 参数**，于是服务端 LogOutController 走 sanitizeReturnUrl('' , base) 分支，
 * 直接 302 回 base（也就是首页）。这就是「在论坛/博客任意页面退出后被弹回首页」的根因。
 *
 * 好消息：服务端**已经原生支持** ?return=，不需要改 vendor、也不需要新增路由：
 *   · vendor/flarum/core/src/Forum/Controller/LogOutController.php
 *       $returnUrl = Arr::get($request->getQueryParams(), 'return');
 *       $return = $this->sanitizeReturnUrl((string) $returnUrl, $base);
 *   · sanitizeReturnUrl() 会用 Laminas\Diactoros\Uri 解析后校验 host 是否在
 *     「config url 的 host + redirectDomains 配置」白名单内，不在就回落 base。
 *     → 所以这里必须传**完整地址**（window.location.href），不能只传 `/blog` 这种路径：
 *       纯路径的 getHost() 是空字符串，白名单必然不通过，仍会跳首页。
 *
 * 因此本文件新增 override('flarum/common/Session', 'logout', ...)：保留核心的表单提交方式
 * （CSRF、cookie 清理、rememberer 全在服务端处理，行为与核心完全一致），只是把 action 改成
 * `{baseUrl}/logout?return=<encodeURIComponent(当前页完整地址)>`。
 *
 * 为什么 Session 可以用字符串路径直接 override（区别于 LogInModal 的懒加载）：
 *   Session 属于 common bundle 的同步模块，页面初始化时已注册在 export registry 里；
 *   override(字符串路径) 内部走 flarum.reg.onLoad()，模块已存在会**立刻**回调，不存在则排队。
 *   两种情形都能正确生效，且不需要在源码里 import Session（避免编译成 flarum.reg.get 的空值）。
 */
import app from 'flarum/forum/app';
import { override } from 'flarum/common/extend';

/**
 * LogInModal 的注册表路径（字符串形式）。
 *
 * 说明：这里刻意写成字符串而不是 `import LogInModal from '...'`，
 * 因为该模块是懒加载 chunk，直接 import 拿到的是 undefined（详见文件头「背景三」）。
 * 该字符串会被 common/extend.ts 的 namespaceAndIdFromPath() 解析成
 * namespace='core'、id='forum/components/LogInModal'。
 */
const LOGIN_MODAL_PATH = 'flarum/forum/components/LogInModal';

/**
 * 判断 detail 是否是「不该给用户看的原始异常文本」。
 *
 * 与首页 nav.php::jnSafeErrorMessage() 的 isRaw 判定保持同一口径（FQ-045）：
 * Flarum 在 debug 模式下会把「异常类名 + 完整 PHP 堆栈（含服务器绝对路径）」写进 detail，
 * 这类文本绝不能出现在页面上。核心自身的 429（裸 FloodingException）正好命中此特征，
 * 于是我们会跳过覆盖、保留核心那句通用限流提示。
 */
function looksLikeRawException(detail) {
  return detail.indexOf('Stack trace') !== -1 || /\.php:\d+/.test(detail) || /[A-Za-z]:\\/.test(detail) || /\\[A-Za-z_][A-Za-z0-9_]*\\/.test(detail);
}

/**
 * 安全解码 detail。
 *
 * 核心在 requestErrorCatch() 里使用 decodeURI(detail) 来还原 \n 等转义字符，
 * 但它**没有 try/catch**：若文案里含有不构成合法转义序列的裸 `%`，decodeURI 会抛 URIError
 * 并打断整个错误处理链。这里我们自己解码，失败时原样返回，绝不冒这个风险。
 * （服务端那侧也有对应约定：错误文案里禁止出现裸 `%`。）
 */
function safeDecode(raw) {
  try {
    return decodeURI(raw);
  } catch (e) {
    return raw;
  }
}

/**
 * Session 的注册表路径（字符串形式）。
 *
 * 写成字符串而不是 `import Session from 'flarum/common/Session'`：
 * 该 import 会被 webpack 编译成 flarum.reg.get('core','common/Session')，
 * 虽然当前是同步模块能取到值，但用字符串路径更稳妥（模块未注册时会排队等待），
 * 且与 LOGIN_MODAL_PATH 的写法保持一致，避免后续 Flarum 调整分包策略时失效。
 */
const SESSION_PATH = 'flarum/common/Session';

/**
 * 安装「显示剩余封禁时间」的补丁。
 */
export default function showRemainingLockoutTime() {
  // 用字符串路径 + override：既能在「弹窗已被加载过」时立刻生效，
  // 也能在「chunk 尚未加载」时排队，等首次打开登录弹窗时自动生效。
  override(LOGIN_MODAL_PATH, 'onerror', function (original, error) {
    try {
      if (error?.status === 429 && error.alert) {
        const raw = error.response?.errors?.[0]?.detail;
        if (typeof raw === 'string' && raw !== '') {
          const detail = safeDecode(raw);

          // 干净可读的文案才用于覆盖；原始堆栈一律不显示（保留核心的通用提示）
          if (detail !== '' && !looksLikeRawException(detail)) {
            error.alert.content = detail;
          }
        }
      }
    } catch (e) {
      /* 忽略：本扩展是体验增强，任何意外都不能影响登录主流程 */
    }

    // 原样交回核心实现（它会处理 401 口令错误提示，并把 alert 显示到弹窗上）
    return original(error);
  });
}

/**
 * 安装「退出登录后停留在当前页」的补丁。
 *
 * 实现方式：完整复刻核心 Session.logout() 的表单提交逻辑（保证 CSRF / cookie 清理 /
 * remember me 失效等行为与服务端既有流程 100% 一致），仅在 action 上追加 return 参数：
 *   POST {baseUrl}/logout?return=<encodeURIComponent(window.location.href)>
 *
 * 服务端（vendor/.../LogOutController.php）会：
 *   1) 校验 host 白名单（config url 的 host + redirectDomains），不通过则回落 base；
 *   2) 校验通过后先 logOut + 删除 access token + 触发 LoggedOut 事件；
 *   3) 最后 302 到 $return（即用户退出前所在的页面）。
 * 因此退出后会「原地刷新」为游客态，而不是弹回首页。
 *
 * 容错：整个替换逻辑包在 try/catch 里，任何一步出错（例如未来核心改了属性名）
 * 都会退回 original()（核心原实现，最坏情况只是回到旧行为「跳首页」，绝不会退不出去）。
 */
export function stayOnCurrentPageAfterLogout() {
  override(SESSION_PATH, 'logout', function (original) {
    try {
      const baseUrl = app.forum.attribute('baseUrl');
      // 必须是完整地址（协议+host+path+query），否则服务端 host 白名单校验不通过
      const currentUrl = window.location.href;
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `${baseUrl}/logout?return=${encodeURIComponent(currentUrl)}`;

      // CSRF token 仍然放在请求体里（与核心一致），return 走 query string
      const tokenField = document.createElement('input');
      tokenField.type = 'hidden';
      tokenField.name = 'csrfToken';
      tokenField.value = this.csrfToken;
      form.appendChild(tokenField);
      document.body.appendChild(form);
      form.submit();

      // 已接管，不再调用核心实现（否则会再提交一次不带 return 的表单）
      return;
    } catch (e) {
      // 兜底：退回核心实现（旧行为：退出后回首页），保证退出功能本身不失效
      return original();
    }
  });
}

// 初始化器 name 必须与扩展 ID 对应（本包 ID 为 jiedh/login-guard → CLI 形式 jiedh-login-guard）。
// 不设置优先级：本扩展与其它扩展互不影响，使用默认顺序即可。
app.initializers.add('jiedh-login-guard', () => {
  showRemainingLockoutTime();
  stayOnCurrentPageAfterLogout();
});